import { SubjectArea } from 'features/subjectArea/types';
import { SagaIterator } from 'redux-saga';
import { put, retry, select } from 'redux-saga/effects';
import { Context } from 'features/app/types';
import { getAppliedGradepoints, getBenchmarkSetName } from 'features/app/redux/context';
import {
	fetchAttainmentProfileData,
	SubjectAreaAttainmentProfileDataResponse,
} from '../../../../api/subjectAreaAPI';
import { flattenResponse } from '../../../../utils/agGridUtils/agGridComparisonsHelpers';
import { setPageLoading } from '../attainmentProfile/slice';
import { fetchAttainmentLoading } from '../attainmentProfile/selectors';
import { getUdfTypeAndValue } from 'features/subjectPage/redux';

/**
 * Saga that fetches attainment profile grid and graph data with one request.
 * @param subjectAreaPages Current Subject Area
 * @param examLevel Current ExamLevel
 * @param subjectId Current Subject Id
 */
export function* attainmentProfileData(
	subjectAreaPages: SubjectArea.SubjectAnalysisPages,
	examLevel: CommonFeatureTypes.ExamLevels,
	subjectId: number
): SagaIterator<void> {
	const isLoading: boolean = yield select(fetchAttainmentLoading);
	if (isLoading) return;

	yield put(setPageLoading(true));

	const gradepoints: Context.Gradepoint[] = yield select(getAppliedGradepoints);
	const benchmarkSet: string = yield select(getBenchmarkSetName);
	const udfTypeAndValue: { type: string; value: string } = yield select(getUdfTypeAndValue);

	try {
		const SECOND = 1000;
		const response: ResponseBuilder<SubjectAreaAttainmentProfileDataResponse> = yield retry(
			3,
			10 * SECOND,
			fetchAttainmentProfileData,
			subjectAreaPages,
			examLevel,
			benchmarkSet,
			gradepoints,
			[subjectId],
			udfTypeAndValue
		);

		if (!response || response.hasError) {
			const errorMessage = !response
				? 'Attainment profile data is empty'
				: (response.errors[0] as any).message || response.errors[0].Message;

			yield put({
				type: `subjectAreaAttainmentProfile/setMainGridDataFail`,
				payload: errorMessage,
			});
			yield put({
				type: `subjectAreaAttainmentProfile/setMainGraphDataFail`,
				payload: errorMessage,
			});
			yield put({
				type: `subjectAreaAttainmentProfile/setSagaFlowStatus`,
				payload: true,
			});
			return;
		}

		yield put({
			type: `subjectAreaAttainmentProfile/setMainGridDataFail`,
			payload: undefined,
		});
		yield put({
			type: `subjectAreaAttainmentProfile/setMainGraphDataFail`,
			payload: undefined,
		});

		const flatten = {
			...response.responseObject.grid,
			rows: flattenResponse(response.responseObject.grid.rows),
		};

		yield put({
			type: `subjectAreaAttainmentProfile/setMainGridDataSuccess`,
			payload: flatten,
		});
		yield put({
			type: `subjectAreaAttainmentProfile/setMainGraphDataSuccess`,
			payload: response.responseObject.graph,
		});
		yield put({
			type: `subjectAreaAttainmentProfile/setSagaFlowStatus`,
			payload: false,
		});
	} catch (e) {
		yield put({
			type: `subjectAreaAttainmentProfile/setMainGridDataFail`,
			payload: e,
		});
		yield put({
			type: `subjectAreaAttainmentProfile/setMainGraphDataFail`,
			payload: e,
		});
	} finally {
		// Guaranteed to run on success, error, AND saga cancellation — ensures the
		// loading spinner is never left stuck if the task is cancelled
		yield put(setPageLoading(false));
	}
}
